import { InvoiceKind } from '@/context/services/invoice_kind/InvoiceKind.service';
import { useTranslation } from '@/i18n';
import {
  FormSelect,
  FormSelectProps,
  SelectProps,
} from '@moneyforward/ap-frontend-components';
import { memo, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';

export type InputType = {
  anyId: string | undefined;
};

type Options = {
  label: string;
  value: string;
};

export type Props<TFieldValues extends FieldValues> = {
  selectProps?: Omit<FormSelectProps<TFieldValues>['selectProps'], 'options'>;
  isNotTarget: boolean;
} & Omit<FormSelectProps<TFieldValues>, 'selectProps' | 'label'>;

const _InvoiceKindSelect = <TFieldValues extends FieldValues>({
  selectProps: _selectProps,
  isNotTarget,
  ...rest
}: Props<TFieldValues>) => {
  const { t } = useTranslation();

  const invoiceKindOption: Options[] = useMemo(() => {
    if (isNotTarget) {
      return [
        { value: InvoiceKind.not_target, label: t('invoice_kind.not_target') },
      ];
    }

    return [
      { value: InvoiceKind.qualified, label: t('invoice_kind.qualified') },
      {
        value: InvoiceKind.unqualified_eighty,
        label: t('invoice_kind.unqualified_eighty'),
      },
      {
        value: InvoiceKind.unqualified_seventy,
        label: t('invoice_kind.unqualified_seventy'),
      },
    ];
  }, [isNotTarget, t]);

  const selectProps: SelectProps = useMemo(() => {
    return {
      onSearchDebounceWait: 600,
      comboBox: false,
      placeholder: '',
      inputPlaceholder: '',
      ..._selectProps,
      options: invoiceKindOption,
      mode: 'single',
      selectAll: undefined,
      selectAllLabel: undefined,
      isAllowClear: false,
    };
  }, [_selectProps, invoiceKindOption]);

  return <FormSelect<TFieldValues> selectProps={selectProps} {...rest} />;
};
_InvoiceKindSelect.displayName = '_InvoiceKindSelect';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const InvoiceKindSelect = memo((props) => (
  <_InvoiceKindSelect {...props} />
)) as typeof _InvoiceKindSelect;
InvoiceKindSelect.displayName = 'InvoiceKindSelect';
